// src/core/bodySerializer.gen.ts
var serializeFormDataPair = (data, key, value) => {
  if (typeof value === "string" || value instanceof Blob) {
    data.append(key, value);
  } else if (value instanceof Date) {
    data.append(key, value.toISOString());
  } else {
    data.append(key, JSON.stringify(value));
  }
};
var formDataBodySerializer = {
  bodySerializer: (body) => {
    const data = new FormData;
    Object.entries(body).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((v) => serializeFormDataPair(data, key, v));
      } else {
        serializeFormDataPair(data, key, value);
      }
    });
    return data;
  }
};
var jsonBodySerializer = {
  bodySerializer: (body) => JSON.stringify(body, (_key, value) => typeof value === "bigint" ? value.toString() : value)
};
// src/core/params.gen.ts
var extraPrefixesMap = {
  $body_: "body",
  $headers_: "headers",
  $path_: "path",
  $query_: "query"
};
var extraPrefixes = Object.entries(extraPrefixesMap);
// src/core/serverSentEvents.gen.ts
var createSseClient = ({
  onRequest,
  onSseError,
  onSseEvent,
  responseTransformer,
  responseValidator,
  sseDefaultRetryDelay,
  sseMaxRetryAttempts,
  sseMaxRetryDelay,
  sseSleepFn,
  url,
  ...options
}) => {
  let lastEventId;
  const sleep = sseSleepFn ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const createStream = async function* () {
    let retryDelay = sseDefaultRetryDelay ?? 3000;
    let attempt = 0;
    const signal = options.signal ?? new AbortController().signal;
    while (true) {
      if (signal.aborted)
        break;
      attempt++;
      const headers = options.headers instanceof Headers ? options.headers : new Headers(options.headers);
      if (lastEventId !== undefined) {
        headers.set("Last-Event-ID", lastEventId);
      }
      try {
        const requestInit = {
          redirect: "follow",
          ...options,
          body: options.serializedBody,
          headers,
          signal
        };
        let request = new Request(url, requestInit);
        if (onRequest) {
          request = await onRequest(url, requestInit);
        }
        const _fetch = options.fetch ?? globalThis.fetch;
        const response = await _fetch(request);
        if (!response.ok)
          throw new Error(`SSE failed: ${response.status} ${response.statusText}`);
        if (!response.body)
          throw new Error("No body in SSE response");
        const reader = response.body.pipeThrough(new TextDecoderStream).getReader();
        let buffer = "";
        const abortHandler = () => {
          try {
            reader.cancel();
          } catch {}
        };
        signal.addEventListener("abort", abortHandler);
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done)
              break;
            buffer += value;
            buffer = buffer.replace(/\r\n/g, `
`).replace(/\r/g, `
`);
            const chunks = buffer.split(`

`);
            buffer = chunks.pop() ?? "";
            for (const chunk of chunks) {
              const lines = chunk.split(`
`);
              const dataLines = [];
              let eventName;
              for (const line of lines) {
                if (line.startsWith("data:")) {
                  dataLines.push(line.replace(/^data:\s*/, ""));
                } else if (line.startsWith("event:")) {
                  eventName = line.replace(/^event:\s*/, "");
                } else if (line.startsWith("id:")) {
                  lastEventId = line.replace(/^id:\s*/, "");
                } else if (line.startsWith("retry:")) {
                  const parsed = Number.parseInt(line.replace(/^retry:\s*/, ""), 10);
                  if (!Number.isNaN(parsed)) {
                    retryDelay = parsed;
                  }
                }
              }
              let data;
              let parsedJson = false;
              if (dataLines.length) {
                const rawData = dataLines.join(`
`);
                try {
                  data = JSON.parse(rawData);
                  parsedJson = true;
                } catch {
                  data = rawData;
                }
              }
              if (parsedJson) {
                if (responseValidator) {
                  await responseValidator(data);
                }
                if (responseTransformer) {
                  data = await responseTransformer(data);
                }
              }
              onSseEvent?.({
                data,
                event: eventName,
                id: lastEventId,
                retry: retryDelay
              });
              if (dataLines.length) {
                yield data;
              }
            }
          }
        } finally {
          signal.removeEventListener("abort", abortHandler);
          reader.releaseLock();
        }
        break;
      } catch (error) {
        onSseError?.(error);
        if (sseMaxRetryAttempts !== undefined && attempt >= sseMaxRetryAttempts) {
          break;
        }
        const backoff = Math.min(retryDelay * 2 ** (attempt - 1), sseMaxRetryDelay ?? 30000);
        await sleep(backoff);
      }
    }
  };
  const stream = createStream();
  return { stream };
};

// src/core/pathSerializer.gen.ts
var separatorArrayExplode = (style) => {
  switch (style) {
    case "label":
      return ".";
    case "matrix":
      return ";";
    case "simple":
      return ",";
    default:
      return "&";
  }
};
var separatorArrayNoExplode = (style) => {
  switch (style) {
    case "form":
      return ",";
    case "pipeDelimited":
      return "|";
    case "spaceDelimited":
      return "%20";
    default:
      return ",";
  }
};
var separatorObjectExplode = (style) => {
  switch (style) {
    case "label":
      return ".";
    case "matrix":
      return ";";
    case "simple":
      return ",";
    default:
      return "&";
  }
};
var serializeArrayParam = ({
  allowReserved,
  explode,
  name,
  style,
  value
}) => {
  if (!explode) {
    const joinedValues2 = (allowReserved ? value : value.map((v) => encodeURIComponent(v))).join(separatorArrayNoExplode(style));
    switch (style) {
      case "label":
        return `.${joinedValues2}`;
      case "matrix":
        return `;${name}=${joinedValues2}`;
      case "simple":
        return joinedValues2;
      default:
        return `${name}=${joinedValues2}`;
    }
  }
  const separator = separatorArrayExplode(style);
  const joinedValues = value.map((v) => {
    if (style === "label" || style === "simple") {
      return allowReserved ? v : encodeURIComponent(v);
    }
    return serializePrimitiveParam({
      allowReserved,
      name,
      value: v
    });
  }).join(separator);
  return style === "label" || style === "matrix" ? separator + joinedValues : joinedValues;
};
var serializePrimitiveParam = ({
  allowReserved,
  name,
  value
}) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "object") {
    throw new Error("Deeply-nested arrays/objects aren’t supported. Provide your own `querySerializer()` to handle these.");
  }
  return `${name}=${allowReserved ? value : encodeURIComponent(value)}`;
};
var serializeObjectParam = ({
  allowReserved,
  explode,
  name,
  style,
  value,
  valueOnly
}) => {
  if (value instanceof Date) {
    return valueOnly ? value.toISOString() : `${name}=${value.toISOString()}`;
  }
  if (style !== "deepObject" && !explode) {
    let values = [];
    Object.entries(value).forEach(([key, v]) => {
      values = [...values, key, allowReserved ? v : encodeURIComponent(v)];
    });
    const joinedValues2 = values.join(",");
    switch (style) {
      case "form":
        return `${name}=${joinedValues2}`;
      case "label":
        return `.${joinedValues2}`;
      case "matrix":
        return `;${name}=${joinedValues2}`;
      default:
        return joinedValues2;
    }
  }
  const separator = separatorObjectExplode(style);
  const joinedValues = Object.entries(value).map(([key, v]) => serializePrimitiveParam({
    allowReserved,
    name: style === "deepObject" ? `${name}[${key}]` : key,
    value: v
  })).join(separator);
  return style === "label" || style === "matrix" ? separator + joinedValues : joinedValues;
};

// src/core/utils.gen.ts
var PATH_PARAM_RE = /\{[^{}]+\}/g;
var defaultPathSerializer = ({ path, url: _url }) => {
  let url = _url;
  const matches = _url.match(PATH_PARAM_RE);
  if (matches) {
    for (const match of matches) {
      let explode = false;
      let name = match.substring(1, match.length - 1);
      let style = "simple";
      if (name.endsWith("*")) {
        explode = true;
        name = name.substring(0, name.length - 1);
      }
      if (name.startsWith(".")) {
        name = name.substring(1);
        style = "label";
      } else if (name.startsWith(";")) {
        name = name.substring(1);
        style = "matrix";
      }
      const value = path[name];
      if (value === undefined || value === null) {
        continue;
      }
      if (Array.isArray(value)) {
        url = url.replace(match, serializeArrayParam({ explode, name, style, value }));
        continue;
      }
      if (typeof value === "object") {
        url = url.replace(match, serializeObjectParam({
          explode,
          name,
          style,
          value,
          valueOnly: true
        }));
        continue;
      }
      if (style === "matrix") {
        url = url.replace(match, `;${serializePrimitiveParam({
          name,
          value
        })}`);
        continue;
      }
      const replaceValue = encodeURIComponent(style === "label" ? `.${value}` : value);
      url = url.replace(match, replaceValue);
    }
  }
  return url;
};
var getUrl = ({
  baseUrl,
  path,
  query,
  querySerializer,
  url: _url
}) => {
  const pathUrl = _url.startsWith("/") ? _url : `/${_url}`;
  let url = (baseUrl ?? "") + pathUrl;
  if (path) {
    url = defaultPathSerializer({ path, url });
  }
  let search = query ? querySerializer(query) : "";
  if (search.startsWith("?")) {
    search = search.substring(1);
  }
  if (search) {
    url += `?${search}`;
  }
  return url;
};
function getValidRequestBody(options) {
  const hasBody = options.body !== undefined;
  const isSerializedBody = hasBody && options.bodySerializer;
  if (isSerializedBody) {
    if ("serializedBody" in options) {
      const hasSerializedBody = options.serializedBody !== undefined && options.serializedBody !== "";
      return hasSerializedBody ? options.serializedBody : null;
    }
    return options.body !== "" ? options.body : null;
  }
  if (hasBody) {
    return options.body;
  }
  return;
}

// src/core/auth.gen.ts
var getAuthToken = async (auth, callback) => {
  const token = typeof callback === "function" ? await callback(auth) : callback;
  if (!token) {
    return;
  }
  if (auth.scheme === "bearer") {
    return `Bearer ${token}`;
  }
  if (auth.scheme === "basic") {
    return `Basic ${btoa(token)}`;
  }
  return token;
};

// src/client/utils.gen.ts
var createQuerySerializer = ({
  parameters = {},
  ...args
} = {}) => {
  const querySerializer = (queryParams) => {
    const search = [];
    if (queryParams && typeof queryParams === "object") {
      for (const name in queryParams) {
        const value = queryParams[name];
        if (value === undefined || value === null) {
          continue;
        }
        const options = parameters[name] || args;
        if (Array.isArray(value)) {
          const serializedArray = serializeArrayParam({
            allowReserved: options.allowReserved,
            explode: true,
            name,
            style: "form",
            value,
            ...options.array
          });
          if (serializedArray)
            search.push(serializedArray);
        } else if (typeof value === "object") {
          const serializedObject = serializeObjectParam({
            allowReserved: options.allowReserved,
            explode: true,
            name,
            style: "deepObject",
            value,
            ...options.object
          });
          if (serializedObject)
            search.push(serializedObject);
        } else {
          const serializedPrimitive = serializePrimitiveParam({
            allowReserved: options.allowReserved,
            name,
            value
          });
          if (serializedPrimitive)
            search.push(serializedPrimitive);
        }
      }
    }
    return search.join("&");
  };
  return querySerializer;
};
var getParseAs = (contentType) => {
  if (!contentType) {
    return "stream";
  }
  const cleanContent = contentType.split(";")[0]?.trim();
  if (!cleanContent) {
    return;
  }
  if (cleanContent.startsWith("application/json") || cleanContent.endsWith("+json")) {
    return "json";
  }
  if (cleanContent === "multipart/form-data") {
    return "formData";
  }
  if (["application/", "audio/", "image/", "video/"].some((type) => cleanContent.startsWith(type))) {
    return "blob";
  }
  if (cleanContent.startsWith("text/")) {
    return "text";
  }
  return;
};
var checkForExistence = (options, name) => {
  if (!name) {
    return false;
  }
  if (options.headers.has(name) || options.query?.[name] || options.headers.get("Cookie")?.includes(`${name}=`)) {
    return true;
  }
  return false;
};
var setAuthParams = async ({
  security,
  ...options
}) => {
  for (const auth of security) {
    if (checkForExistence(options, auth.name)) {
      continue;
    }
    const token = await getAuthToken(auth, options.auth);
    if (!token) {
      continue;
    }
    const name = auth.name ?? "Authorization";
    switch (auth.in) {
      case "query":
        if (!options.query) {
          options.query = {};
        }
        options.query[name] = token;
        break;
      case "cookie":
        options.headers.append("Cookie", `${name}=${token}`);
        break;
      case "header":
      default:
        options.headers.set(name, token);
        break;
    }
  }
};
var buildUrl = (options) => getUrl({
  baseUrl: options.baseUrl,
  path: options.path,
  query: options.query,
  querySerializer: typeof options.querySerializer === "function" ? options.querySerializer : createQuerySerializer(options.querySerializer),
  url: options.url
});
var mergeConfigs = (a, b) => {
  const config = { ...a, ...b };
  if (config.baseUrl?.endsWith("/")) {
    config.baseUrl = config.baseUrl.substring(0, config.baseUrl.length - 1);
  }
  config.headers = mergeHeaders(a.headers, b.headers);
  return config;
};
var headersEntries = (headers) => {
  const entries = [];
  headers.forEach((value, key) => {
    entries.push([key, value]);
  });
  return entries;
};
var mergeHeaders = (...headers) => {
  const mergedHeaders = new Headers;
  for (const header of headers) {
    if (!header) {
      continue;
    }
    const iterator = header instanceof Headers ? headersEntries(header) : Object.entries(header);
    for (const [key, value] of iterator) {
      if (value === null) {
        mergedHeaders.delete(key);
      } else if (Array.isArray(value)) {
        for (const v of value) {
          mergedHeaders.append(key, v);
        }
      } else if (value !== undefined) {
        mergedHeaders.set(key, typeof value === "object" ? JSON.stringify(value) : value);
      }
    }
  }
  return mergedHeaders;
};

class Interceptors {
  fns = [];
  clear() {
    this.fns = [];
  }
  eject(id) {
    const index = this.getInterceptorIndex(id);
    if (this.fns[index]) {
      this.fns[index] = null;
    }
  }
  exists(id) {
    const index = this.getInterceptorIndex(id);
    return Boolean(this.fns[index]);
  }
  getInterceptorIndex(id) {
    if (typeof id === "number") {
      return this.fns[id] ? id : -1;
    }
    return this.fns.indexOf(id);
  }
  update(id, fn) {
    const index = this.getInterceptorIndex(id);
    if (this.fns[index]) {
      this.fns[index] = fn;
      return id;
    }
    return false;
  }
  use(fn) {
    this.fns.push(fn);
    return this.fns.length - 1;
  }
}
var createInterceptors = () => ({
  error: new Interceptors,
  request: new Interceptors,
  response: new Interceptors
});
var defaultQuerySerializer = createQuerySerializer({
  allowReserved: false,
  array: {
    explode: true,
    style: "form"
  },
  object: {
    explode: true,
    style: "deepObject"
  }
});
var defaultHeaders = {
  "Content-Type": "application/json"
};
var createConfig = (override = {}) => ({
  ...jsonBodySerializer,
  headers: defaultHeaders,
  parseAs: "auto",
  querySerializer: defaultQuerySerializer,
  ...override
});

// src/client/client.gen.ts
var createClient = (config = {}) => {
  let _config = mergeConfigs(createConfig(), config);
  const getConfig = () => ({ ..._config });
  const setConfig = (config2) => {
    _config = mergeConfigs(_config, config2);
    return getConfig();
  };
  const interceptors = createInterceptors();
  const beforeRequest = async (options) => {
    const opts = {
      ..._config,
      ...options,
      fetch: options.fetch ?? _config.fetch ?? globalThis.fetch,
      headers: mergeHeaders(_config.headers, options.headers),
      serializedBody: undefined
    };
    if (opts.security) {
      await setAuthParams({
        ...opts,
        security: opts.security
      });
    }
    if (opts.requestValidator) {
      await opts.requestValidator(opts);
    }
    if (opts.body !== undefined && opts.bodySerializer) {
      opts.serializedBody = opts.bodySerializer(opts.body);
    }
    if (opts.body === undefined || opts.serializedBody === "") {
      opts.headers.delete("Content-Type");
    }
    const url = buildUrl(opts);
    return { opts, url };
  };
  const request = async (options) => {
    const { opts, url } = await beforeRequest(options);
    const requestInit = {
      redirect: "follow",
      ...opts,
      body: getValidRequestBody(opts)
    };
    let request2 = new Request(url, requestInit);
    for (const fn of interceptors.request.fns) {
      if (fn) {
        request2 = await fn(request2, opts);
      }
    }
    const _fetch = opts.fetch;
    let response;
    try {
      response = await _fetch(request2);
    } catch (error2) {
      let finalError2 = error2;
      for (const fn of interceptors.error.fns) {
        if (fn) {
          finalError2 = await fn(error2, undefined, request2, opts);
        }
      }
      finalError2 = finalError2 || {};
      if (opts.throwOnError) {
        throw finalError2;
      }
      return opts.responseStyle === "data" ? undefined : {
        error: finalError2,
        request: request2,
        response: undefined
      };
    }
    for (const fn of interceptors.response.fns) {
      if (fn) {
        response = await fn(response, request2, opts);
      }
    }
    const result = {
      request: request2,
      response
    };
    if (response.ok) {
      const parseAs = (opts.parseAs === "auto" ? getParseAs(response.headers.get("Content-Type")) : opts.parseAs) ?? "json";
      if (response.status === 204 || response.headers.get("Content-Length") === "0") {
        let emptyData;
        switch (parseAs) {
          case "arrayBuffer":
          case "blob":
          case "text":
            emptyData = await response[parseAs]();
            break;
          case "formData":
            emptyData = new FormData;
            break;
          case "stream":
            emptyData = response.body;
            break;
          case "json":
          default:
            emptyData = {};
            break;
        }
        return opts.responseStyle === "data" ? emptyData : {
          data: emptyData,
          ...result
        };
      }
      let data;
      switch (parseAs) {
        case "arrayBuffer":
        case "blob":
        case "formData":
        case "text":
          data = await response[parseAs]();
          break;
        case "json": {
          const text = await response.text();
          data = text ? JSON.parse(text) : {};
          break;
        }
        case "stream":
          return opts.responseStyle === "data" ? response.body : {
            data: response.body,
            ...result
          };
      }
      if (parseAs === "json") {
        if (opts.responseValidator) {
          await opts.responseValidator(data);
        }
        if (opts.responseTransformer) {
          data = await opts.responseTransformer(data);
        }
      }
      return opts.responseStyle === "data" ? data : {
        data,
        ...result
      };
    }
    const textError = await response.text();
    let jsonError;
    try {
      jsonError = JSON.parse(textError);
    } catch {}
    const error = jsonError ?? textError;
    let finalError = error;
    for (const fn of interceptors.error.fns) {
      if (fn) {
        finalError = await fn(error, response, request2, opts);
      }
    }
    finalError = finalError || {};
    if (opts.throwOnError) {
      throw finalError;
    }
    return opts.responseStyle === "data" ? undefined : {
      error: finalError,
      ...result
    };
  };
  const makeMethodFn = (method) => (options) => request({ ...options, method });
  const makeSseFn = (method) => async (options) => {
    const { opts, url } = await beforeRequest(options);
    return createSseClient({
      ...opts,
      body: opts.body,
      headers: opts.headers,
      method,
      onRequest: async (url2, init) => {
        let request2 = new Request(url2, init);
        for (const fn of interceptors.request.fns) {
          if (fn) {
            request2 = await fn(request2, opts);
          }
        }
        return request2;
      },
      serializedBody: getValidRequestBody(opts),
      url
    });
  };
  return {
    buildUrl,
    connect: makeMethodFn("CONNECT"),
    delete: makeMethodFn("DELETE"),
    get: makeMethodFn("GET"),
    getConfig,
    head: makeMethodFn("HEAD"),
    interceptors,
    options: makeMethodFn("OPTIONS"),
    patch: makeMethodFn("PATCH"),
    post: makeMethodFn("POST"),
    put: makeMethodFn("PUT"),
    request,
    setConfig,
    sse: {
      connect: makeSseFn("CONNECT"),
      delete: makeSseFn("DELETE"),
      get: makeSseFn("GET"),
      head: makeSseFn("HEAD"),
      options: makeSseFn("OPTIONS"),
      patch: makeSseFn("PATCH"),
      post: makeSseFn("POST"),
      put: makeSseFn("PUT"),
      trace: makeSseFn("TRACE")
    },
    trace: makeMethodFn("TRACE")
  };
};
// src/client.gen.ts
var client = createClient(createConfig());

// src/sdk.gen.ts
var listYourOrganizations = (options) => (options?.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/",
  ...options
});
var retrieveAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/",
  ...options
});
var updateAnOrganization = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deprecatedListAnOrganization_sMetricAlertRules = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/alert-rules/",
  ...options
});
var deprecatedCreateAMetricAlertRuleForAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/alert-rules/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deprecatedDeleteAMetricAlertRule = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/alert-rules/{alert_rule_id}/",
  ...options
});
var deprecatedRetrieveAMetricAlertRuleForAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/alert-rules/{alert_rule_id}/",
  ...options
});
var deprecatedUpdateAMetricAlertRule = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/alert-rules/{alert_rule_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var getIntegrationProviderInformation = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/config/integrations/",
  ...options
});
var listAnOrganization_sCustomDashboards = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/dashboards/",
  ...options
});
var createANewDashboardForAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/dashboards/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnOrganization_sCustomDashboard = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/dashboards/{dashboard_id}/",
  ...options
});
var retrieveAnOrganization_sCustomDashboard = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/dashboards/{dashboard_id}/",
  ...options
});
var editAnOrganization_sCustomDashboard = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/dashboards/{dashboard_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var bulkDeleteMonitors = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/",
  ...options
});
var fetchAnOrganization_sMonitors = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/",
  ...options
});
var createAMonitorForAProject = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var mutateAnOrganization_sMonitors = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAMonitor = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/{detector_id}/",
  ...options
});
var fetchAMonitor = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/{detector_id}/",
  ...options
});
var updateAMonitorById = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/detectors/{detector_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sDiscoverSavedQueries = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/discover/saved/",
  ...options
});
var createANewSavedQuery = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/discover/saved/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnOrganization_sDiscoverSavedQuery = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/discover/saved/{query_id}/",
  ...options
});
var retrieveAnOrganization_sDiscoverSavedQuery = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/discover/saved/{query_id}/",
  ...options
});
var editAnOrganization_sDiscoverSavedQuery = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/discover/saved/{query_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sEnvironments = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/environments/",
  ...options
});
var resolveAnEventId = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/eventids/{event_id}/",
  ...options
});
var queryExploreEventsInTableFormat = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/events/",
  ...options
});
var queryExploreEventsInTimeseriesFormat = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/events-timeseries/",
  ...options
});
var createAnExternalUser = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/external-users/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnExternalUser = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/external-users/{external_user_id}/",
  ...options
});
var updateAnExternalUser = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/external-users/{external_user_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveDataForwardersForAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/forwarding/",
  ...options
});
var createADataForwarderForAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/forwarding/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteADataForwarderForAnOrganization = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/forwarding/{data_forwarder_id}/",
  ...options
});
var updateADataForwarderForAnOrganization = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/forwarding/{data_forwarder_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sAvailableIntegrations = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/integrations/",
  ...options
});
var deleteAnIntegrationForAnOrganization = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/integrations/{integration_id}/",
  ...options
});
var retrieveAnIntegrationForAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/integrations/{integration_id}/",
  ...options
});
var bulkRemoveAnOrganization_sIssues = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/",
  ...options
});
var listAnOrganization_sIssues = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/",
  ...options
});
var bulkMutateAnOrganization_sIssues = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sMembers = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/",
  ...options
});
var addAMemberToAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnOrganizationMember = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/",
  ...options
});
var retrieveAnOrganizationMember = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/",
  ...options
});
var updateAnOrganizationMember_sRoles = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnOrganizationMemberFromATeam = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/teams/{team_id_or_slug}/",
  ...options
});
var addAnOrganizationMemberToATeam = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/teams/{team_id_or_slug}/",
  ...options
});
var updateAnOrganizationMember_sTeamRole = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/members/{member_id}/teams/{team_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveMonitorsForAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/",
  ...options
});
var createAMonitor = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAMonitorOrMonitorEnvironments = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options
});
var retrieveAMonitor = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options
});
var updateAMonitor = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveCheckInsForAMonitor = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/monitors/{monitor_id_or_slug}/checkins/",
  ...options
});
var listSpikeProtectionNotifications = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/notifications/actions/",
  ...options
});
var createASpikeProtectionNotificationAction = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/notifications/actions/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteASpikeProtectionNotificationAction = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/notifications/actions/{action_id}/",
  ...options
});
var retrieveASpikeProtectionNotificationAction = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/notifications/actions/{action_id}/",
  ...options
});
var updateASpikeProtectionNotificationAction = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/notifications/actions/{action_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveInstallInfoForAGivenArtifact = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/preprodartifacts/{artifact_id}/install-details/",
  ...options
});
var retrieveSizeAnalysisResultsForAGivenArtifact = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/preprodartifacts/{artifact_id}/size-analysis/",
  ...options
});
var retrievesListOfRepositoriesForAGivenOwner = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repositories/",
  ...options
});
var getsSyncingStatusForRepositoriesForAnIntegratedOrg = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repositories/sync/",
  ...options
});
var syncsRepositoriesFromAnIntegratedOrgWithGitHub = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repositories/sync/",
  ...options
});
var retrievesAPaginatedListOfRepositoryTokensForAGivenOwner = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repositories/tokens/",
  ...options
});
var retrievesASingleRepositoryForAGivenOwner = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/",
  ...options
});
var retrievesListOfBranchesForAGivenOwnerAndRepository = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/branches/",
  ...options
});
var retrievePaginatedListOfTestResultsForRepository_Owner_AndOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/test-results/",
  ...options
});
var retrieveAggregatedTestResultMetricsForRepository_Owner_AndOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/test-results-aggregates/",
  ...options
});
var retrieveTestSuitesBelongingToARepository_sTestResults = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/test-suites/",
  ...options
});
var regeneratesARepositoryUploadTokenAndReturnsTheNewToken = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/prevent/owner/{owner}/repository/{repository}/token/regenerate/",
  ...options
});
var listAnOrganization_sClientKeys = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/project-keys/",
  ...options
});
var listAnOrganization_sProjects = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/projects/",
  ...options
});
var listAnOrganization_sTrustedRelays = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/relay_usage/",
  ...options
});
var retrieveStatusesOfReleaseThresholdsAlpha = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/release-threshold-statuses/",
  ...options
});
var deleteAnOrganization_sRelease = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/",
  ...options
});
var retrieveAnOrganization_sRelease = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/",
  ...options
});
var updateAnOrganization_sRelease = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listARelease_sDeploys = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/deploys/",
  ...options
});
var createADeploy = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/deploys/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveACountOfReplaysForAGivenIssueOrTransaction = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/replay-count/",
  ...options
});
var listAnOrganization_sSelectors = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/replay-selectors/",
  ...options
});
var listAnOrganization_sReplays = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/replays/",
  ...options
});
var retrieveAReplayInstance = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/replays/{replay_id}/",
  ...options
});
var listARepository_sCommits = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/repos/{repo_id}/commits/",
  ...options
});
var listAnOrganization_sPaginatedTeams = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Groups",
  ...options
});
var provisionANewTeam = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Groups",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnIndividualTeam = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Groups/{team_id_or_slug}",
  ...options
});
var queryAnIndividualTeam = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Groups/{team_id_or_slug}",
  ...options
});
var updateATeam_sAttributes = (options) => (options.client ?? client).patch({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Groups/{team_id_or_slug}",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sScimMembers = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Users",
  ...options
});
var provisionANewOrganizationMember = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Users",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnOrganizationMemberViaScim = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Users/{member_id}",
  ...options
});
var queryAnIndividualOrganizationMember = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Users/{member_id}",
  ...options
});
var updateAnOrganizationMember_sAttributes = (options) => (options.client ?? client).patch({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/scim/v2/Users/{member_id}",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveTheCustomIntegrationsCreatedByAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/sentry-apps/",
  ...options
});
var retrieveReleaseHealthSessionStatistics = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/sessions/",
  ...options
});
var resolveAShortId = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/shortids/{issue_id}/",
  ...options
});
var retrieveAnOrganization_sEventsCountByProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/stats-summary/",
  ...options
});
var retrieveEventCountsForAnOrganizationV2 = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/stats_v2/",
  ...options
});
var listAnOrganization_sTeams = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/teams/",
  ...options
});
var createANewTeam = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/teams/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAUser_sTeamsForAnOrganization = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/user-teams/",
  ...options
});
var bulkDeleteAlerts = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/",
  ...options
});
var fetchAlerts = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/",
  ...options
});
var createAnAlertForAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var mutateAnOrganization_sAlerts = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnAlert = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/{workflow_id}/",
  ...options
});
var fetchAnAlert = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/{workflow_id}/",
  ...options
});
var updateAnAlertById = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/workflows/{workflow_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAProject = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/",
  ...options
});
var retrieveAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/",
  ...options
});
var updateAProject = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sEnvironments = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/environments/",
  ...options
});
var retrieveAProjectEnvironment = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/environments/{environment}/",
  ...options
});
var updateAProjectEnvironment = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/environments/{environment}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sErrorEvents = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/events/",
  ...options
});
var debugIssuesRelatedToSourceMapsForAGivenEvent = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/events/{event_id}/source-map-debug/",
  ...options
});
var listAProject_sDataFilters = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/filters/",
  ...options
});
var updateAnInboundDataFilter = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/filters/{filter_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sClientKeys = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/keys/",
  ...options
});
var createANewClientKey = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/keys/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAClientKey = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/keys/{key_id}/",
  ...options
});
var retrieveAClientKey = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/keys/{key_id}/",
  ...options
});
var updateAClientKey = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/keys/{key_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sOrganizationMembers = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/members/",
  ...options
});
var deleteAMonitorOrMonitorEnvironmentsForAProject = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options
});
var retrieveAMonitorForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options
});
var updateAMonitorForAProject = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/monitors/{monitor_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveCheckInsForAMonitorByProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/monitors/{monitor_id_or_slug}/checkins/",
  ...options
});
var retrieveOwnershipConfigurationForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/ownership/",
  ...options
});
var updateOwnershipConfigurationForAProject = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/ownership/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var getTheLatestInstallableBuildForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/preprodartifacts/build-distribution/latest/",
  ...options
});
var deleteAReplayInstance = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/{replay_id}/",
  ...options
});
var listClickedNodes = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/{replay_id}/clicks/",
  ...options
});
var listRecordingSegments = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/{replay_id}/recording-segments/",
  ...options
});
var retrieveARecordingSegment = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/{replay_id}/recording-segments/{segment_id}/",
  ...options
});
var listUsersWhoHaveViewedAReplay = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/{replay_id}/viewed-by/",
  ...options
});
var listReplayBatchDeletionJobs = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/jobs/delete/",
  ...options
});
var createReplayBatchDeletionJob = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/jobs/delete/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveAReplayBatchDeletionJob = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/replays/jobs/delete/{job_id}/",
  ...options
});
var deprecatedListAProject_sIssueAlertRules = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/rules/",
  ...options
});
var deprecatedCreateAnIssueAlertRuleForAProject = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/rules/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deprecatedDeleteAnIssueAlertRule = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/rules/{rule_id}/",
  ...options
});
var deprecatedRetrieveAnIssueAlertRuleForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/rules/{rule_id}/",
  ...options
});
var deprecatedUpdateAnIssueAlertRule = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/rules/{rule_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteASymbolSourceFromAProject = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/symbol-sources/",
  ...options
});
var retrieveAProject_sSymbolSources = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/symbol-sources/",
  ...options
});
var addASymbolSourceToAProject = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/symbol-sources/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var updateAProject_sSymbolSource = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/symbol-sources/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sTeams = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/teams/",
  ...options
});
var deleteATeamFromAProject = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/teams/{team_id_or_slug}/",
  ...options
});
var addATeamToAProject = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/teams/{team_id_or_slug}/",
  ...options
});
var listSeerAiModels = (options) => (options?.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/seer/models/",
  ...options
});
var deleteACustomIntegration = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/sentry-apps/{sentry_app_id_or_slug}/",
  ...options
});
var retrieveACustomIntegrationByIdOrSlug = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/sentry-apps/{sentry_app_id_or_slug}/",
  ...options
});
var updateAnExistingCustomIntegration = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/sentry-apps/{sentry_app_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteATeam = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/",
  ...options
});
var retrieveATeam = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/",
  ...options
});
var updateATeam = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var createAnExternalTeam = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/external-teams/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnExternalTeam = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/external-teams/{external_team_id}/",
  ...options
});
var updateAnExternalTeam = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/external-teams/{external_team_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listATeam_sMembers = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/members/",
  ...options
});
var listATeam_sProjects = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/projects/",
  ...options
});
var createANewProject = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/teams/{organization_id_or_slug}/{team_id_or_slug}/projects/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sRepositories = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/repos/",
  ...options
});
var deleteASpecificProject_sDebugInformationFile = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/files/dsyms/",
  ...options
});
var listAProject_sDebugInformationFiles = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/files/dsyms/",
  ...options
});
var uploadANewFile = (options) => (options.client ?? client).post({
  ...formDataBodySerializer,
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/files/dsyms/",
  ...options,
  headers: {
    "Content-Type": null,
    ...options.headers
  }
});
var listAProject_sUsers = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/users/",
  ...options
});
var listATag_sValues = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/tags/{key}/values/",
  ...options
});
var retrieveEventCountsForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/stats/",
  ...options
});
var listAProject_sUserFeedback = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/user-feedback/",
  ...options
});
var submitUserFeedback = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/user-feedback/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAProject_sServiceHooks = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/hooks/",
  ...options
});
var registerANewServiceHook = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/hooks/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var removeAServiceHook = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/hooks/{hook_id}/",
  ...options
});
var retrieveAServiceHook = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/hooks/{hook_id}/",
  ...options
});
var updateAServiceHook = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/hooks/{hook_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveAnEventForAProject = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/events/{event_id}/",
  ...options
});
var bulkRemoveAListOfIssues = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/issues/",
  ...options
});
var listAProject_sIssues = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/issues/",
  ...options
});
var bulkMutateAListOfIssues = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/issues/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listATag_sValuesForAnIssue = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/tags/{key}/values/",
  ...options
});
var listAnIssue_sHashes = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/hashes/",
  ...options
});
var removeAnIssue = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/",
  ...options
});
var retrieveAnIssue = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/",
  ...options
});
var updateAnIssue = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sReleases = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/",
  ...options
});
var createANewReleaseForAnOrganization = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganization_sReleaseFiles = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/files/",
  ...options
});
var uploadANewOrganizationReleaseFile = (options) => (options.client ?? client).post({
  ...formDataBodySerializer,
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/files/",
  ...options,
  headers: {
    "Content-Type": null,
    ...options.headers
  }
});
var listAProject_sReleaseFiles = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/files/",
  ...options
});
var uploadANewProjectReleaseFile = (options) => (options.client ?? client).post({
  ...formDataBodySerializer,
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/files/",
  ...options,
  headers: {
    "Content-Type": null,
    ...options.headers
  }
});
var deleteAnOrganizationRelease_sFile = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options
});
var retrieveAnOrganizationRelease_sFile = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options
});
var updateAnOrganizationReleaseFile = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAProjectRelease_sFile = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options
});
var retrieveAProjectRelease_sFile = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options
});
var updateAProjectReleaseFile = (options) => (options.client ?? client).put({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/files/{file_id}/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnOrganizationRelease_sCommits = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/commits/",
  ...options
});
var listAProjectRelease_sCommits = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/projects/{organization_id_or_slug}/{project_id_or_slug}/releases/{version}/commits/",
  ...options
});
var retrieveFilesChangedInARelease_sCommits = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/releases/{version}/commitfiles/",
  ...options
});
var listAnOrganization_sIntegrationPlatformInstallations = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/sentry-app-installations/",
  ...options
});
var createOrUpdateAnExternalIssue = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/sentry-app-installations/{uuid}/external-issues/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var deleteAnExternalIssue = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/sentry-app-installations/{uuid}/external-issues/{external_issue_id}/",
  ...options
});
var disableSpikeProtection = (options) => (options.client ?? client).delete({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/spike-protections/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var enableSpikeProtection = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/spike-protections/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var retrieveSeerIssueFixState = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/autofix/",
  ...options
});
var startSeerIssueFix = (options) => (options.client ?? client).post({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/autofix/",
  ...options,
  headers: {
    "Content-Type": "application/json",
    ...options.headers
  }
});
var listAnIssue_sEvents = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/events/",
  ...options
});
var retrieveAnIssueEvent = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/events/{event_id}/",
  ...options
});
var retrieveCustomIntegrationIssueLinksForTheGivenSentryIssue = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/external-issues/",
  ...options
});
var retrieveTagDetails = (options) => (options.client ?? client).get({
  security: [{ scheme: "bearer", type: "http" }],
  url: "/api/0/organizations/{organization_id_or_slug}/issues/{issue_id}/tags/{key}/",
  ...options
});
// src/sentry-pagination.ts
var parseSentryLinkHeader = (header) => {
  if (!header) {
    return {};
  }
  const segments = header.split(",");
  for (const segment of segments) {
    const parts = segment.trim().split(";").map((s) => s.trim());
    let rel;
    let results;
    let cursor;
    for (const part of parts) {
      const relMatch = part.match(/^rel="([^"]*)"$/);
      if (relMatch) {
        rel = relMatch[1];
        continue;
      }
      const resultsMatch = part.match(/^results="([^"]*)"$/);
      if (resultsMatch) {
        results = resultsMatch[1];
        continue;
      }
      const cursorMatch = part.match(/^cursor="([^"]*)"$/);
      if (cursorMatch) {
        cursor = cursorMatch[1];
        continue;
      }
    }
    if (rel === "next" && results === "true" && cursor) {
      return { nextCursor: cursor };
    }
  }
  return {};
};
var unwrapResult = (result, context) => {
  if (result.error !== undefined) {
    throw new Error(`${context}: API request failed: ${JSON.stringify(result.error)}`);
  }
  return { data: result.data, response: result.response };
};
var unwrapPaginatedResult = (result, context) => {
  const { data, response } = unwrapResult(result, context);
  const linkHeader = response.headers.get("link");
  const { nextCursor } = parseSentryLinkHeader(linkHeader);
  return { data, nextCursor };
};
var paginateAll = async (fetcher, context, options) => {
  const maxPages = options?.maxPages ?? 50;
  const allItems = [];
  let cursor;
  for (let page = 0;page < maxPages; page++) {
    const result = await fetcher(cursor);
    const { data, nextCursor } = unwrapPaginatedResult(result, context);
    allItems.push(...data);
    if (!nextCursor) {
      break;
    }
    cursor = nextCursor;
  }
  return allItems;
};
export {
  uploadANewProjectReleaseFile,
  uploadANewOrganizationReleaseFile,
  uploadANewFile,
  updateOwnershipConfigurationForAProject,
  updateAnOrganization_sRelease,
  updateAnOrganizationReleaseFile,
  updateAnOrganizationMember_sTeamRole,
  updateAnOrganizationMember_sRoles,
  updateAnOrganizationMember_sAttributes,
  updateAnOrganization,
  updateAnIssue,
  updateAnInboundDataFilter,
  updateAnExternalUser,
  updateAnExternalTeam,
  updateAnExistingCustomIntegration,
  updateAnAlertById,
  updateATeam_sAttributes,
  updateATeam,
  updateASpikeProtectionNotificationAction,
  updateAServiceHook,
  updateAProject_sSymbolSource,
  updateAProjectReleaseFile,
  updateAProjectEnvironment,
  updateAProject,
  updateAMonitorForAProject,
  updateAMonitorById,
  updateAMonitor,
  updateADataForwarderForAnOrganization,
  updateAClientKey,
  unwrapResult,
  unwrapPaginatedResult,
  syncsRepositoriesFromAnIntegratedOrgWithGitHub,
  submitUserFeedback,
  startSeerIssueFix,
  retrievesListOfRepositoriesForAGivenOwner,
  retrievesListOfBranchesForAGivenOwnerAndRepository,
  retrievesASingleRepositoryForAGivenOwner,
  retrievesAPaginatedListOfRepositoryTokensForAGivenOwner,
  retrieveTheCustomIntegrationsCreatedByAnOrganization,
  retrieveTestSuitesBelongingToARepository_sTestResults,
  retrieveTagDetails,
  retrieveStatusesOfReleaseThresholdsAlpha,
  retrieveSizeAnalysisResultsForAGivenArtifact,
  retrieveSeerIssueFixState,
  retrieveReleaseHealthSessionStatistics,
  retrievePaginatedListOfTestResultsForRepository_Owner_AndOrganization,
  retrieveOwnershipConfigurationForAProject,
  retrieveMonitorsForAnOrganization,
  retrieveInstallInfoForAGivenArtifact,
  retrieveFilesChangedInARelease_sCommits,
  retrieveEventCountsForAnOrganizationV2,
  retrieveEventCountsForAProject,
  retrieveDataForwardersForAnOrganization,
  retrieveCustomIntegrationIssueLinksForTheGivenSentryIssue,
  retrieveCheckInsForAMonitorByProject,
  retrieveCheckInsForAMonitor,
  retrieveAnOrganization_sRelease,
  retrieveAnOrganization_sEventsCountByProject,
  retrieveAnOrganization_sDiscoverSavedQuery,
  retrieveAnOrganization_sCustomDashboard,
  retrieveAnOrganizationRelease_sFile,
  retrieveAnOrganizationMember,
  retrieveAnOrganization,
  retrieveAnIssueEvent,
  retrieveAnIssue,
  retrieveAnIntegrationForAnOrganization,
  retrieveAnEventForAProject,
  retrieveAggregatedTestResultMetricsForRepository_Owner_AndOrganization,
  retrieveATeam,
  retrieveASpikeProtectionNotificationAction,
  retrieveAServiceHook,
  retrieveAReplayInstance,
  retrieveAReplayBatchDeletionJob,
  retrieveARecordingSegment,
  retrieveAProject_sSymbolSources,
  retrieveAProjectRelease_sFile,
  retrieveAProjectEnvironment,
  retrieveAProject,
  retrieveAMonitorForAProject,
  retrieveAMonitor,
  retrieveACustomIntegrationByIdOrSlug,
  retrieveACountOfReplaysForAGivenIssueOrTransaction,
  retrieveAClientKey,
  resolveAnEventId,
  resolveAShortId,
  removeAnIssue,
  removeAServiceHook,
  registerANewServiceHook,
  regeneratesARepositoryUploadTokenAndReturnsTheNewToken,
  queryExploreEventsInTimeseriesFormat,
  queryExploreEventsInTableFormat,
  queryAnIndividualTeam,
  queryAnIndividualOrganizationMember,
  provisionANewTeam,
  provisionANewOrganizationMember,
  parseSentryLinkHeader,
  paginateAll,
  mutateAnOrganization_sMonitors,
  mutateAnOrganization_sAlerts,
  listYourOrganizations,
  listUsersWhoHaveViewedAReplay,
  listSpikeProtectionNotifications,
  listSeerAiModels,
  listReplayBatchDeletionJobs,
  listRecordingSegments,
  listClickedNodes,
  listAnOrganization_sTrustedRelays,
  listAnOrganization_sTeams,
  listAnOrganization_sSelectors,
  listAnOrganization_sScimMembers,
  listAnOrganization_sRepositories,
  listAnOrganization_sReplays,
  listAnOrganization_sReleases,
  listAnOrganization_sReleaseFiles,
  listAnOrganization_sProjects,
  listAnOrganization_sPaginatedTeams,
  listAnOrganization_sMembers,
  listAnOrganization_sIssues,
  listAnOrganization_sIntegrationPlatformInstallations,
  listAnOrganization_sEnvironments,
  listAnOrganization_sDiscoverSavedQueries,
  listAnOrganization_sCustomDashboards,
  listAnOrganization_sClientKeys,
  listAnOrganization_sAvailableIntegrations,
  listAnOrganizationRelease_sCommits,
  listAnIssue_sHashes,
  listAnIssue_sEvents,
  listAUser_sTeamsForAnOrganization,
  listATeam_sProjects,
  listATeam_sMembers,
  listATag_sValuesForAnIssue,
  listATag_sValues,
  listARepository_sCommits,
  listARelease_sDeploys,
  listAProject_sUsers,
  listAProject_sUserFeedback,
  listAProject_sTeams,
  listAProject_sServiceHooks,
  listAProject_sReleaseFiles,
  listAProject_sOrganizationMembers,
  listAProject_sIssues,
  listAProject_sErrorEvents,
  listAProject_sEnvironments,
  listAProject_sDebugInformationFiles,
  listAProject_sDataFilters,
  listAProject_sClientKeys,
  listAProjectRelease_sCommits,
  getsSyncingStatusForRepositoriesForAnIntegratedOrg,
  getTheLatestInstallableBuildForAProject,
  getIntegrationProviderInformation,
  fetchAnOrganization_sMonitors,
  fetchAnAlert,
  fetchAlerts,
  fetchAMonitor,
  enableSpikeProtection,
  editAnOrganization_sDiscoverSavedQuery,
  editAnOrganization_sCustomDashboard,
  disableSpikeProtection,
  deprecatedUpdateAnIssueAlertRule,
  deprecatedUpdateAMetricAlertRule,
  deprecatedRetrieveAnIssueAlertRuleForAProject,
  deprecatedRetrieveAMetricAlertRuleForAnOrganization,
  deprecatedListAnOrganization_sMetricAlertRules,
  deprecatedListAProject_sIssueAlertRules,
  deprecatedDeleteAnIssueAlertRule,
  deprecatedDeleteAMetricAlertRule,
  deprecatedCreateAnIssueAlertRuleForAProject,
  deprecatedCreateAMetricAlertRuleForAnOrganization,
  deleteAnOrganization_sRelease,
  deleteAnOrganization_sDiscoverSavedQuery,
  deleteAnOrganization_sCustomDashboard,
  deleteAnOrganizationRelease_sFile,
  deleteAnOrganizationMemberViaScim,
  deleteAnOrganizationMemberFromATeam,
  deleteAnOrganizationMember,
  deleteAnIntegrationForAnOrganization,
  deleteAnIndividualTeam,
  deleteAnExternalUser,
  deleteAnExternalTeam,
  deleteAnExternalIssue,
  deleteAnAlert,
  deleteATeamFromAProject,
  deleteATeam,
  deleteASymbolSourceFromAProject,
  deleteASpikeProtectionNotificationAction,
  deleteASpecificProject_sDebugInformationFile,
  deleteAReplayInstance,
  deleteAProjectRelease_sFile,
  deleteAProject,
  deleteAMonitorOrMonitorEnvironmentsForAProject,
  deleteAMonitorOrMonitorEnvironments,
  deleteAMonitor,
  deleteADataForwarderForAnOrganization,
  deleteACustomIntegration,
  deleteAClientKey,
  debugIssuesRelatedToSourceMapsForAGivenEvent,
  createReplayBatchDeletionJob,
  createOrUpdateAnExternalIssue,
  createAnExternalUser,
  createAnExternalTeam,
  createAnAlertForAnOrganization,
  createASpikeProtectionNotificationAction,
  createANewTeam,
  createANewSavedQuery,
  createANewReleaseForAnOrganization,
  createANewProject,
  createANewDashboardForAnOrganization,
  createANewClientKey,
  createAMonitorForAProject,
  createAMonitor,
  createADeploy,
  createADataForwarderForAnOrganization,
  bulkRemoveAnOrganization_sIssues,
  bulkRemoveAListOfIssues,
  bulkMutateAnOrganization_sIssues,
  bulkMutateAListOfIssues,
  bulkDeleteMonitors,
  bulkDeleteAlerts,
  addAnOrganizationMemberToATeam,
  addATeamToAProject,
  addASymbolSourceToAProject,
  addAMemberToAnOrganization
};
